import React from 'react';
import {
    CalendarIcon, GridIcon, HangerIcon, HomeIcon, SearchIcon, ShirtIcon,
    SparklesIcon, TableIcon,
} from '../icons';
import {MOCK_AI_CARD, MOCK_ITEMS, MOCK_NAV, MOCK_PHONE, MOCK_TABS} from './content';
import {GarmentTile, RatingDots} from './visuals';

/**
 * Мокап продукта для hero и секции «Как работает»: окно приложения, телефон с
 * образом дня и карточка AI-рекомендации. Это иллюстрация, собранная вёрсткой,
 * а не скриншот, поэтому она живёт в обеих темах и не устаревает вместе с UI.
 * Для скринридеров блок скрыт целиком — смысл передан текстом секции.
 */

const NAV_ICONS = [HomeIcon, ShirtIcon, SparklesIcon, HangerIcon, GridIcon, CalendarIcon];

const BrowserChrome: React.FC = () => (
    <div className="flex items-center gap-1.5 px-4 h-9 border-b border-ink-100 dark:border-white/5 bg-ink-50 dark:bg-ink-900">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400/80"/>
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80"/>
        <span className="w-2.5 h-2.5 rounded-full bg-brand-400/80"/>
    </div>
);

const MockSidebar: React.FC = () => (
    <div className="hidden sm:flex flex-col gap-1 w-32 lg:w-36 shrink-0 p-3 border-r border-ink-100 dark:border-white/5">
        <div className="flex items-center gap-2 px-1 pb-3">
            <span className="w-6 h-6 rounded-lg bg-brand-500 flex items-center justify-center">
                <ShirtIcon className="w-3.5 h-3.5 text-ink-900"/>
            </span>
            <span className="text-[11px] font-semibold text-ink-900 dark:text-ink-50">Clothes</span>
        </div>
        {MOCK_NAV.map((label, index) => {
            const Icon = NAV_ICONS[index] ?? TableIcon;
            const active = index === 0;
            return (
                <div
                    key={label}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-[11px] ${
                        active
                            ? 'bg-brand-500/12 text-brand-700 dark:text-brand-300 font-medium'
                            : 'text-ink-500 dark:text-ink-400'
                    }`}
                >
                    <Icon className="w-3.5 h-3.5"/>
                    <span className="truncate">{label}</span>
                </div>
            );
        })}
    </div>
);

const MockToolbar: React.FC = () => (
    <>
        <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-ink-900 dark:text-ink-50">Все вещи</span>
            <span className="ml-auto flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-ink-50 dark:bg-white/5 text-[11px] text-ink-400 w-32 sm:w-44">
                <SearchIcon className="w-3.5 h-3.5 shrink-0"/>
                <span className="truncate">Поиск по шкафу</span>
            </span>
        </div>
        <div className="flex gap-1.5 mt-3 overflow-hidden">
            {MOCK_TABS.map((tab, index) => (
                <span
                    key={tab}
                    className={`px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap ${
                        index === 0
                            ? 'bg-ink-900 text-white dark:bg-white dark:text-ink-900'
                            : 'bg-ink-50 text-ink-500 dark:bg-white/5 dark:text-ink-400'
                    }`}
                >
                    {tab}
                </span>
            ))}
        </div>
    </>
);

/**
 * Сетка вещей. `animated` включает каскад выкладывания — он нужен только в
 * hero: в секции «Как работает» тот же мокап стоит статично, чтобы страница не
 * превратилась в набор одинаковых выездов.
 */
const MockGrid: React.FC<{animated?: boolean}> = ({animated = false}) => (
    <div className="grid grid-cols-4 gap-2 mt-3">
        {MOCK_ITEMS.map((item, index) => (
            <div
                key={item.id}
                className={`rounded-xl border border-ink-100 dark:border-white/5 p-1.5 ${animated ? 'fw-assemble' : ''}`}
                style={animated ? {animationDelay: `${240 + index * 45}ms`} : undefined}
            >
                <GarmentTile kind={item.kind} className="aspect-[4/5] w-full"/>
                <div className="mt-1.5 px-0.5">
                    <RatingDots value={item.rating}/>
                </div>
            </div>
        ))}
    </div>
);

/** Окно приложения: сайдбар, тулбар и сетка карточек вещей. */
export const BrowserMock: React.FC<{className?: string; animated?: boolean}> = ({className = '', animated = false}) => (
    <div
        className={`rounded-2xl border border-ink-100 dark:border-white/10 bg-white dark:bg-ink-800 shadow-card overflow-hidden ${className}`}>
        <BrowserChrome/>
        <div className="flex min-w-0">
            <MockSidebar/>
            <div className="flex-1 min-w-0 p-3 sm:p-4">
                <MockToolbar/>
                <MockGrid animated={animated}/>
            </div>
        </div>
    </div>
);

/** Телефон с образом дня — вертикальный срез того же гардероба. */
export const PhoneMock: React.FC<{className?: string}> = ({className = ''}) => (
    <div
        className={`rounded-[1.75rem] border-[6px] border-ink-900 dark:border-ink-700 bg-white dark:bg-ink-800 shadow-card-hover overflow-hidden ${className}`}>
        <div className="h-4 flex items-center justify-center">
            <span className="w-10 h-1 rounded-full bg-ink-200 dark:bg-white/10"/>
        </div>
        <div className="px-3 pb-3">
            <div className="text-[11px] font-semibold text-ink-900 dark:text-ink-50">{MOCK_PHONE.title}</div>
            <div className="text-[10px] text-ink-400">{MOCK_PHONE.subtitle}</div>
            <div className="mt-2 space-y-1.5">
                <GarmentTile kind="top" className="h-14 w-full"/>
                <GarmentTile kind="bottom" className="h-14 w-full"/>
                <GarmentTile kind="shoes" className="h-10 w-full"/>
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] text-ink-500 dark:text-ink-400">
                <span>{MOCK_PHONE.temp}</span>
                <span className="badge badge-success">AI</span>
            </div>
            <div className="mt-2 rounded-lg bg-ink-900 dark:bg-white text-white dark:text-ink-900 text-[10px] font-medium text-center py-1.5">
                {MOCK_PHONE.save}
            </div>
        </div>
    </div>
);

/** Всплывающая подсказка AI поверх окна приложения. */
export const AiHintCard: React.FC<{className?: string}> = ({className = ''}) => (
    <div
        className={`rounded-xl border border-ink-100 dark:border-white/10 bg-white dark:bg-ink-800 shadow-card-hover p-3 w-48 ${className}`}>
        <div className="flex items-center gap-1.5">
            <SparklesIcon className="w-3.5 h-3.5 text-brand-500"/>
            <span className="text-[11px] font-semibold text-ink-900 dark:text-ink-50">{MOCK_AI_CARD.title}</span>
        </div>
        <div className="mt-1 text-[10px] text-ink-400">{MOCK_AI_CARD.subtitle}</div>
        <div className="mt-1 text-[11px] font-semibold text-brand-600 dark:text-brand-400">{MOCK_AI_CARD.match}</div>
        <div className="mt-2 grid grid-cols-4 gap-1">
            <GarmentTile kind="top" className="h-8"/>
            <GarmentTile kind="outer" className="h-8"/>
            <GarmentTile kind="bottom" className="h-8"/>
            <GarmentTile kind="bag" className="h-8"/>
        </div>
    </div>
);

/**
 * Композиция hero: окно приложения + телефон + карточка AI.
 *
 * Единственный авторский motion-момент страницы (см. `.fw-assemble` в
 * index.css): окно проявляется, вещи выкладываются каскадом, подсказка AI и
 * телефон приходят со своих сторон — каждый оттуда, где он лежит. Эпизод
 * играет один раз при загрузке и укладывается примерно в секунду.
 */
const AppMockup: React.FC = () => (
    <div className="relative w-full min-w-0" data-testid="landing-app-mockup" aria-hidden="true">
        <BrowserMock className="w-full fw-assemble" animated/>
        {/* Классы несут и задержку: обёртка с transform стала бы containing
            block для этих absolute-элементов и увела бы их с места. */}
        <AiHintCard className="hidden md:block absolute left-2 lg:left-6 -bottom-8 fw-assemble-left"/>
        <PhoneMock className="hidden sm:block absolute -right-2 md:-right-4 -bottom-10 w-32 md:w-36 fw-assemble-right"/>
    </div>
);

export default AppMockup;
